import {
  ComponentType,
  CSSProperties,
  useEffect,
  useRef,
  useState,
} from 'react';
import { EMPTY, fromEvent } from 'rxjs';
import {
  filter,
  map,
  pluck,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IconGripHorizontal } from '@rossum/ui/icons/tabler';
import { Box, darken, lighten, SvgIcon } from '@rossum/ui/material';
import { getMaxHeight, MIN_FOOTER_HEIGHT } from './config';
import { getAdjustedHeight } from './helpers';
import styles from './style.module.sass';

type Props = {
  minHeight: number;
  height?: number;
  setHeight?: (_height: number) => void;
  style?: CSSProperties;
  resetKey?: string | number;
};

type WrappedProps = {
  resizing: boolean;
  style: CSSProperties;
  height: number;
};

const makeResizable =
  <T,>(Wrapped: ComponentType<T & WrappedProps>) =>
  (props: Props & T) => {
    const { setHeight, height = props.minHeight, minHeight, resetKey } = props;

    const ref = useRef<HTMLDivElement>(null);

    const [ownHeight, setOwnHeight] = useState(
      getAdjustedHeight(height, minHeight)
    );

    const [prevResetKey, setPrevResetKey] = useState(resetKey);
    if (resetKey !== prevResetKey) {
      setPrevResetKey(resetKey);
      setOwnHeight(getAdjustedHeight(height, minHeight));
    }

    const [resizing, setResizing] = useState(false);

    useEffect(() => {
      const mousedown = ref.current
        ? fromEvent(ref.current, 'mousedown')
        : EMPTY;
      const mouseup = fromEvent(document, 'mouseup');
      const mousemove = fromEvent(document, 'mousemove');

      const resizeSubscription = mousedown
        .pipe(
          switchMap(() =>
            mousemove.pipe(
              tap(event => {
                event.preventDefault();
                event.stopPropagation();
              }),
              map(event => event as MouseEvent),
              pluck('clientY'),
              map((y: number) => window.innerHeight - y),
              filter(_height => _height > MIN_FOOTER_HEIGHT),
              filter(_height => _height < getMaxHeight()),
              takeUntil(mouseup)
            )
          )
        )
        .subscribe(_height => {
          setOwnHeight(_height);
          setResizing(true);
        });

      const mouseUpSubscription = mousedown
        .pipe(
          switchMap(() =>
            mousemove.pipe(
              take(1),
              switchMap(() => mouseup)
            )
          )
        )
        .subscribe(() => {
          setResizing(false);
        });

      return () => {
        resizeSubscription.unsubscribe();
        mouseUpSubscription.unsubscribe();
        if (setHeight) setHeight(0);
      };
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (setHeight) setHeight(ownHeight);
    }, [ownHeight, setHeight]);

    return (
      <div style={{ position: 'relative' }}>
        <Box
          className={styles['ResizeButton']}
          ref={ref}
          sx={{
            backgroundImage: t =>
              t.palette.mode === 'dark'
                ? `linear-gradient(to right, ${t.palette.background.paper}, ${lighten(t.palette.background.paper, 0.15)})`
                : `linear-gradient(to right, ${t.palette.background.paper}, ${darken(t.palette.background.paper, 0.1)})`,
            '> svg': {
              position: 'absolute',
              top: '-3px',
              height: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              opacity: 0.85,
            },
          }}
        >
          <SvgIcon fontSize="medium" color="secondary">
            <IconGripHorizontal />
          </SvgIcon>
        </Box>
        <Wrapped
          {...props}
          resizing={resizing}
          height={ownHeight}
          style={{ ...props.style, height: ownHeight }}
        />
      </div>
    );
  };

export default makeResizable;
